import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { taskEditMutation } from 'ui-io/queries/tasks/task-edit-mutation'
import {
  Button,
  Classes,
  InputGroup,
  Label,
  TagInput,
  TextArea,
} from '@blueprintjs/core'
import { Box, ThemedDialog } from 'ui-components'
import { TaskT } from 'ui-io/task-io/task-t'
import { string } from 'io-ts'
import { TASKS_QUERY_FILTER } from 'ui-io/queries/tasks/task-query'

import toasts from '../../utils/toasts'

type DialogPropsT = {
  task: TaskT
  isOpen: boolean
  onClose: () => void
}

const EditTaskDialog = ({ task, isOpen, onClose }: DialogPropsT) => {
  const [configurationFields, setConfigurationFields] = useState(
    task.configuration,
  )
  const [info, setInfo] = useState(task.info)
  const { tags, priority } = configurationFields
  const queryClient = useQueryClient()
  const mutation = useMutation(taskEditMutation, {
    onSuccess: () => {
      toasts.success(`Task updated successfully`)
      handleOnClosing()
      queryClient.refetchQueries([TASKS_QUERY_FILTER])
    },
    onError: (err) => {
      toasts.error(
        err instanceof Error ? err.message : 'Error in updating task',
      )
      handleOnClosing()
    },
  })

  const handleOnFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setConfigurationFields({
      ...configurationFields,
      [event.target.name]: parseInt(value) || value,
    })
  }

  const handleOnInfoChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = event.target.value
    setInfo({
      ...info,
      [event.target.name]: value,
    })
  }

  const addTagsToConfig = (values: string[]) => {
    setConfigurationFields({
      ...configurationFields,
      tags: values,
    })
  }

  const handleOnClosing = () => {
    setConfigurationFields(task._raw.configuration)
    onClose()
  }

  const handleOnSubmit = () => {
    const payload = {
      info: info || {},
      priority: Number(priority) || 0,
    }
    const { id } = task
    mutation.mutate({
      taskId: id,
      payload,
    })
  }

  return (
    <ThemedDialog
      isOpen={isOpen}
      onClose={handleOnClosing}
      title="Edit Task"
      icon="edit"
    >
      <div className={Classes.DIALOG_BODY}>
        Please fill in below details and click save to update this task.
        <Box mt={2}>
          <Label>Task Id</Label>
          <InputGroup name="taskId" disabled value={task.id} />
        </Box>
        <Box mt={2}>
          <Label>Priority</Label>
          <InputGroup
            type="number"
            name="priority"
            onChange={handleOnFieldChange}
            placeholder="Set priority"
            value={String(priority)}
          />
        </Box>
        <Box mt={2}>
          <Label>Project</Label>
          <InputGroup
            name="project"
            onChange={handleOnInfoChange}
            placeholder="Add a special project Id"
            value={info?.project ?? ''}
          />
        </Box>
        <Box mt={2}>
          <Label>Tags</Label>
          <TagInput
            values={(tags as React.ReactNode[]) || []}
            addOnBlur
            placeholder="Add a tag and press enter ..."
            onChange={(values) => {
              addTagsToConfig(values.filter(string.is))
            }}
          />
          <Box mt={2}>
            <Label>Description</Label>
            <TextArea
              fill
              name="description"
              rows={2}
              placeholder="Add issue description here."
              value={info?.description ?? ''}
              onChange={handleOnInfoChange}
            />
          </Box>
        </Box>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button onClick={handleOnClosing}>Cancel</Button>
          <Button
            intent="primary"
            loading={mutation.isLoading}
            onClick={handleOnSubmit}
          >
            Save
          </Button>
        </div>
      </div>
    </ThemedDialog>
  )
}

export default EditTaskDialog
